type SiocFields = {
	shippedInOriginalContainer: boolean,
	siocQuantity: string,
}

export const SIOC_QUANTITY_ERROR = 'Required and must be 1 or greater when shipped in original container'

// Returns false only when SIOC is checked and the quantity is empty or < 1.
export function isValidSiocQuantity( fields: SiocFields ): boolean {
	if ( !fields.shippedInOriginalContainer ) {
		return true
	}
	const value = Number( fields.siocQuantity )
	return fields.siocQuantity !== '' && Number.isInteger( value ) && value >= 1
}

// SIOC quantity is only sent when shipped in original container; otherwise it is left unset.
export function siocQuantityForPayload( fields: SiocFields ): number | undefined {
	return fields.shippedInOriginalContainer ? Number( fields.siocQuantity ) : undefined
}

type SiocPolybagFields = {
	shippedInOriginalContainer: boolean,
	isPolybagEligible: boolean,
}

// A variant shipped in its original container cannot be polybag eligible (the API rejects
// the combination), so checking SIOC also clears polybag eligibility. Unchecking SIOC leaves
// polybag eligibility as it was.
export function siocToggleChanges( checked: boolean ): Partial<SiocPolybagFields> {
	return checked
		? { shippedInOriginalContainer: true, isPolybagEligible: false }
		: { shippedInOriginalContainer: false }
}

// --- Tiered validation: Draft vs Complete ---
//
// A record needs only identity (+ SIOC/asset) to be saved as a draft. Weight and
// dimensions are what make it "complete" (Numina-eligible). These helpers centralize
// the rules so the create and edit-variant forms share one definition.

export type ValidationError = {
	// Field key matches a form field name so the UI can attach inline errors by key.
	field: string,
	message: string,
}

// The weight/dimension inputs every completeness-aware form collects, as raw strings.
export type CompletenessFields = {
	dimensionLength: string,
	dimensionWidth: string,
	dimensionHeight: string,
	weightAmount: string,
}

// A value counts as present for completeness only when it parses to a number > 0.
// Blank, NaN, zero, and negative values are all treated as "not provided".
function isPositiveNumeric( value: string | number | null | undefined ): boolean {
	if ( value === null || value === undefined || value === '' ) {
		return false
	}
	const num = Number( value )
	return Number.isFinite( num ) && num > 0
}

const COMPLETE_SUFFIX = 'is required to complete this item master'

export const WEIGHT_REQUIRED_ERROR = `Weight ${COMPLETE_SUFFIX}`
export const LENGTH_REQUIRED_ERROR = `Length ${COMPLETE_SUFFIX}`
export const WIDTH_REQUIRED_ERROR = `Width ${COMPLETE_SUFFIX}`
export const HEIGHT_REQUIRED_ERROR = `Height ${COMPLETE_SUFFIX}`

// Returns the completeness-tier errors (weight + each dimension) for raw string fields.
// An empty array means the record is complete. Messages name the field so they read
// clearly both inline and in the footer summary.
export function getCompletenessErrors( fields: CompletenessFields ): ValidationError[] {
	const errors: ValidationError[] = []
	if ( !isPositiveNumeric( fields.weightAmount ) ) {
		errors.push( { field: 'weightAmount', message: WEIGHT_REQUIRED_ERROR } )
	}
	if ( !isPositiveNumeric( fields.dimensionLength ) ) {
		errors.push( { field: 'dimensionLength', message: LENGTH_REQUIRED_ERROR } )
	}
	if ( !isPositiveNumeric( fields.dimensionWidth ) ) {
		errors.push( { field: 'dimensionWidth', message: WIDTH_REQUIRED_ERROR } )
	}
	if ( !isPositiveNumeric( fields.dimensionHeight ) ) {
		errors.push( { field: 'dimensionHeight', message: HEIGHT_REQUIRED_ERROR } )
	}
	return errors
}

// True when weight and all three dimensions are present (form-field form).
export function isComplete( fields: CompletenessFields ): boolean {
	return getCompletenessErrors( fields ).length === 0
}

// Asset records must have an equipment make/model pair selected.
export const ASSET_MAKE_MODEL_ERROR = 'Equipment make and model are required for asset items'

// Collapses a list of validation errors into a field -> message map so forms can
// attach inline errors by field name. First error per field wins.
export function toErrorMap( errors: ValidationError[] ): Record<string, string> {
	const map: Record<string, string> = {}
	for ( const err of errors ) {
		if ( !( err.field in map ) ) {
			map[ err.field ] = err.message
		}
	}
	return map
}

// Payload mapping: only send a dimensions object when all three dimensions are
// present (the backend dimensions input requires length/width/height together).
// Returns undefined for a draft so blanks are omitted rather than sent as 0/NaN.
export function dimensionsForPayload<U>(
	fields: CompletenessFields & { dimensionUnit: U }
): { length: number, width: number, height: number, unit: U } | undefined {
	if (
		!isPositiveNumeric( fields.dimensionLength ) ||
		!isPositiveNumeric( fields.dimensionWidth ) ||
		!isPositiveNumeric( fields.dimensionHeight )
	) {
		return undefined
	}
	return {
		length: Number( fields.dimensionLength ),
		width: Number( fields.dimensionWidth ),
		height: Number( fields.dimensionHeight ),
		unit: fields.dimensionUnit,
	}
}

// Payload mapping: only send a weight object when the amount is present.
export function weightForPayload<U>(
	fields: { weightAmount: string, weightUnit: U }
): { amount: number, unit: U } | undefined {
	if ( !isPositiveNumeric( fields.weightAmount ) ) {
		return undefined
	}
	return { amount: Number( fields.weightAmount ), unit: fields.weightUnit }
}

// Backend create inputs (CreateItemMasterVariantInput / CreateProductItemMasterInput)
// now mark dimensions and weight optional (inventory-api sc-536466 Phase 1), so a draft
// with blank dims/weight persists via the create mutations. A variant saved without
// dims/weight is created with status DRAFT server-side. Completing a draft via Edit
// Variant also works (UpdateItemMasterVariantInput.dimensions/weight are optional).
// NOTE: run `yarn codegen` against staging once Phase 1 is deployed to pull the nullable
// dims/weight (and the new status/isComplete fields); until then the generated types for
// dims/weight are hand-aligned to nullable in src/graphql/index.ts.
export const BACKEND_SUPPORTS_DRAFT_CREATE = true

export const DRAFT_CREATE_UNSUPPORTED_MESSAGE =
	'Saving a draft without weight and dimensions is not available yet. Enter weight and dimensions to create this item master.'

// Shape of a variant as returned by the item master queries (Decimal scalars are `any`).
type VariantDimensionsWeight = {
	dimensions?: { length?: unknown, width?: unknown, height?: unknown } | null,
	weight?: { amount?: unknown } | null,
}

// True when a fetched variant has usable weight and all three dimensions.
// Used to derive the Draft/Incomplete badge on the list and detail views.
export function isVariantComplete( variant: VariantDimensionsWeight | null | undefined ): boolean {
	if ( !variant ) {
		return false
	}
	const d = variant.dimensions
	const w = variant.weight
	return (
		isPositiveNumeric( d?.length as number | string | null | undefined ) &&
		isPositiveNumeric( d?.width as number | string | null | undefined ) &&
		isPositiveNumeric( d?.height as number | string | null | undefined ) &&
		isPositiveNumeric( w?.amount as number | string | null | undefined )
	)
}

// True when an item master's base variant is complete. A record with no base
// variant is treated as incomplete (draft).
export function isItemMasterComplete(
	variants: ( VariantDimensionsWeight & { isBase: boolean } )[] | null | undefined
): boolean {
	if ( !variants || variants.length === 0 ) {
		return false
	}
	const base = variants.find( ( v ) => v.isBase ) ?? variants[ 0 ]
	return isVariantComplete( base )
}
